import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SkuLocationImportResult } from 'returnCenter-api';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faTimesCircle } from '@fortawesome/pro-regular-svg-icons';

type Props = {
    result: SkuLocationImportResult;
    onClose?: () => void;
};

const SkuLocationUploadResult: React.FC<Props> = ({ result, onClose }) => {
    const { t } = useTranslation();
    const hasErrors = result.errors && result.errors.length > 0;
    const isSuccess = result.success && !hasErrors;

    return (
        <Container>
            <Header $success={isSuccess}>
                <FontAwesomeIcon
                    icon={isSuccess ? faCheckCircle : hasErrors ? faExclamationTriangle : faTimesCircle}
                    size="lg"
                />
                <Text className="f3-700" fontSize={16}>
                    {result.message || (isSuccess ? t('admin.uploadSuccess', 'Upload successful') : t('admin.uploadPartial', 'Upload completed with errors'))}
                </Text>
                {onClose && <CloseButton onClick={onClose}>×</CloseButton>}
            </Header>

            <Stats>
                <StatItem>
                    <StatValue>{result.totalRows ?? 0}</StatValue>
                    <StatLabel>{t('admin.totalRows', 'Total rows')}</StatLabel>
                </StatItem>
                <StatItem>
                    <StatValue $success>{result.importedRows ?? 0}</StatValue>
                    <StatLabel>{t('admin.importedRows', 'Imported')}</StatLabel>
                </StatItem>
                <StatItem>
                    <StatValue $warning>{result.skippedRows ?? 0}</StatValue>
                    <StatLabel>{t('admin.skippedRows', 'Skipped')}</StatLabel>
                </StatItem>
                {hasErrors && (
                    <StatItem>
                        <StatValue $error>{result.errors?.length ?? 0}</StatValue>
                        <StatLabel>{t('admin.errors', 'Errors')}</StatLabel>
                    </StatItem>
                )}
            </Stats>

            {result.importedCompanyCodes && Object.keys(result.importedCompanyCodes).length > 0 && (
                <CompanyCodeSection>
                    <CompanyCodeHeader>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <Text className="f3-700" fontSize={14}>
                            {t('admin.importedCompanyCodes', 'Imported per company')}
                        </Text>
                    </CompanyCodeHeader>
                    <CompanyCodeList>
                        {Object.entries(result.importedCompanyCodes).map(([code, count]) => (
                            <CompanyCodeItem key={code}>
                                <strong>{code}:</strong> {count} {t('admin.articles', 'articles')}
                            </CompanyCodeItem>
                        ))}
                    </CompanyCodeList>
                </CompanyCodeSection>
            )}

            {result.skippedCompanyCodes && result.skippedCompanyCodes.length > 0 && (
                <CompanyCodeSection $warning>
                    <CompanyCodeHeader $warning>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <Text className="f3-700" fontSize={14}>
                            {t('admin.skippedCompanyCodes', 'Skipped company codes (no access)')}
                        </Text>
                    </CompanyCodeHeader>
                    <CompanyCodeList>
                        <Text className="f3-400" fontSize={13}>
                            {result.skippedCompanyCodes.join(', ')}
                        </Text>
                    </CompanyCodeList>
                </CompanyCodeSection>
            )}

            {hasErrors && (
                <ErrorSection>
                    <Text className="f3-700" fontSize={14}>
                        {t('admin.errorDetails', 'Error details')}
                    </Text>
                    <ErrorList>
                        {result.errors?.map((error, index) => (
                            <ErrorItem key={index}>
                                <ErrorRow>
                                    <Text className="f3-400" fontSize={12} color="textLight">
                                        {t('admin.row', 'Row')} {error.rowNumber}
                                    </Text>
                                    {error.sku && (
                                        <Text className="f3-400" fontSize={12}>
                                            SKU: <strong>{error.sku}</strong>
                                        </Text>
                                    )}
                                    {error.location && (
                                        <Text className="f3-400" fontSize={12}>
                                            Location: <strong>{error.location}</strong>
                                        </Text>
                                    )}
                                </ErrorRow>
                                <Text className="f3-400" fontSize={12} color="error">
                                    {error.errorMessage}
                                </Text>
                            </ErrorItem>
                        ))}
                    </ErrorList>
                </ErrorSection>
            )}

            {result.items && result.items.length > 0 && (
                <ItemsSection>
                    <Text className="f3-700" fontSize={14}>
                        {t('admin.importedItems', 'Imported items')} ({result.items.length})
                    </Text>
                    <ItemsTable>
                        <thead>
                            <tr>
                                <th>SKU</th>
                                <th>Location</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.items.slice(0, 10).map((item, index) => (
                                <tr key={item.id ?? index}>
                                    <td>{item.sku}</td>
                                    <td>{item.location}</td>
                                </tr>
                            ))}
                            {result.items.length > 10 && (
                                <tr>
                                    <td colSpan={2}>
                                        <Text className="f3-400" fontSize={12} color="textLight">
                                            ... {t('admin.andMore', 'and {{count}} more', { count: result.items.length - 10 })}
                                        </Text>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </ItemsTable>
                </ItemsSection>
            )}
        </Container>
    );
};

const Container = styled.div`
    background: ${({ theme }) => theme.colors.fill};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: var(--radius);
    overflow: hidden;
    max-width: 600px;
    margin-top: 16px;
`;

const Header = styled(Flex)<{ $success?: boolean }>`
    padding: 16px;
    gap: 12px;
    align-items: center;
    background: ${({ theme, $success }) => ($success ? theme.colors.success + '20' : theme.colors.warning + '20')};
    color: ${({ theme, $success }) => ($success ? theme.colors.success : theme.colors.warning)};
`;

const CloseButton = styled.button`
    margin-left: auto;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textLight};
    padding: 0;
    line-height: 1;

    &:hover {
        color: ${({ theme }) => theme.colors.text};
    }
`;

const Stats = styled(Flex)`
    padding: 16px;
    gap: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatItem = styled(Flex)`
    flex-direction: column;
    align-items: center;
`;

const StatValue = styled.span<{ $success?: boolean; $warning?: boolean; $error?: boolean }>`
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme, $success, $warning, $error }) =>
        $success ? theme.colors.success : $warning ? theme.colors.warning : $error ? theme.colors.error : theme.colors.text};
`;

const StatLabel = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textLight};
`;

const ErrorSection = styled.div`
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorList = styled.div`
    margin-top: 8px;
    max-height: 200px;
    overflow-y: auto;
`;

const ErrorItem = styled.div`
    padding: 8px;
    background: ${({ theme }) => theme.colors.error}10;
    border-radius: var(--radius);
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ErrorRow = styled(Flex)`
    gap: 16px;
    margin-bottom: 4px;
`;

const ItemsSection = styled.div`
    padding: 16px;
`;

const CompanyCodeSection = styled.div<{ $warning?: boolean }>`
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme, $warning }) => ($warning ? theme.colors.warning + '10' : theme.colors.success + '10')};
`;

const CompanyCodeHeader = styled(Flex)<{ $warning?: boolean }>`
    gap: 8px;
    align-items: center;
    color: ${({ theme, $warning }) => ($warning ? theme.colors.warning : theme.colors.success)};
    margin-bottom: 8px;
`;

const CompanyCodeList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
`;

const CompanyCodeItem = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text};
`;

const ItemsTable = styled.table`
    width: 100%;
    margin-top: 8px;
    border-collapse: collapse;

    th,
    td {
        padding: 8px 12px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    th {
        font-weight: 600;
        font-size: 12px;
        color: ${({ theme }) => theme.colors.textLight};
        background: ${({ theme }) => theme.colors.fillMedium};
    }

    td {
        font-size: 13px;
    }

    tbody tr:hover {
        background: ${({ theme }) => theme.colors.fillLight};
    }
`;

export default SkuLocationUploadResult;
